/**
 * Deterministic wavetable generators.
 * Synthesize only — never copy Vital factory wave_data.
 *
 * MVP frame size is 256 samples (1 frame for basic shapes).
 * Upstream Vital WaveFrame size is 2048; dogfood matrix verification is pending.
 */
import { bytesToBase64 } from "./base64.js";
export const SAMPLES_PER_FRAME = 256;
export const DEFAULT_FRAMES = 1;
const AUTHOR = "Defibrillator";
function encodeFloat32LE(samples) {
    const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
    return bytesToBase64(bytes);
}
function wrap(name, samples, frames = DEFAULT_FRAMES) {
    return {
        name,
        author: AUTHOR,
        wave_data: encodeFloat32LE(samples),
        num_frames: frames,
        num_samples: samples.length,
    };
}
function fillFrame(fn) {
    const out = new Float32Array(SAMPLES_PER_FRAME);
    for (let i = 0; i < SAMPLES_PER_FRAME; i++) {
        const phase = i / SAMPLES_PER_FRAME;
        out[i] = fn(phase, i);
    }
    return out;
}
/** Unit sine, one cycle. */
export function sine() {
    return wrap("Sine", fillFrame((phase) => Math.sin(phase * Math.PI * 2)));
}
/** Symmetric triangle, one cycle. */
export function triangle() {
    return wrap("Triangle", fillFrame((phase) => {
        const t = phase < 0.5 ? phase * 4 - 1 : 3 - phase * 4;
        return t;
    }));
}
/** Rising saw, one cycle (−1 → 1). */
export function saw() {
    return wrap("Saw", fillFrame((phase) => phase * 2 - 1));
}
/** 50% duty square. */
export function square() {
    return wrap("Square", fillFrame((phase) => (phase < 0.5 ? 1 : -1)));
}
/** Variable-width pulse; width in (0, 1). */
export function pulse(width) {
    const w = Math.min(0.95, Math.max(0.05, width));
    return wrap(`Pulse ${Math.round(w * 100)}`, fillFrame((phase) => (phase < w ? 1 : -1)));
}
/**
 * Seeded LCG noise (Numerical Recipes constants).
 * Same seed always yields the same wave_data.
 */
export function noise(seed = 1) {
    let state = seed >>> 0 || 1;
    return wrap(`Noise ${seed}`, fillFrame(() => {
        state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
        return state / 0x80000000 - 1;
    }));
}
/** Additive harmonic series; harmonics[0] = fundamental amplitude. */
export function harmonic(harmonics) {
    const amps = harmonics.length > 0 ? harmonics : [1];
    return wrap(`Harmonic ${amps.length}`, fillFrame((phase) => {
        let sum = 0;
        let norm = 0;
        for (let n = 0; n < amps.length; n++) {
            const a = amps[n] ?? 0;
            sum += a * Math.sin(phase * Math.PI * 2 * (n + 1));
            norm += Math.abs(a);
        }
        return norm > 0 ? sum / norm : 0;
    }));
}
export function generateBasic(shape) {
    switch (shape) {
        case "sine":
            return sine();
        case "triangle":
            return triangle();
        case "saw":
            return saw();
        case "square":
            return square();
        case "pulse":
            return pulse(0.5);
        case "noise":
            return noise(1);
        case "harmonic":
            return harmonic([1]);
    }
}
//# sourceMappingURL=wavetable.js.map